import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, ROLE_LABELS } from '../context/AuthContext';
import api from '../services/api';
import {
  HiOutlineClipboardList,
  HiOutlineCube,
  HiOutlineUserGroup,
  HiOutlineCurrencyDollar,
  HiOutlineTruck,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineExclamationCircle,
} from 'react-icons/hi';
import './Dashboard.css';

const STATUS_LABELS = {
  created: 'Creada',
  accepted: 'Aceptada',
  preparing: 'Preparando',
  prepared: 'Preparada',
  in_delivery: 'En camino',
  delivered: 'Entregada',
  paid: 'Cobrada',
  closed: 'Cerrada',
  cancelled: 'Cancelada',
};

const STATUS_COLORS = {
  created: '#3b82f6',
  accepted: '#8b5cf6',
  preparing: '#f59e0b',
  prepared: '#06b6d4',
  in_delivery: '#f97316',
  delivered: '#22c55e',
  paid: '#14532d',
  closed: '#64748b',
  cancelled: '#ef4444',
};

const Dashboard = () => {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    orders: { total: 0, byStatus: {} },
    products: { total: 0, lowStock: 0 },
    clients: { total: 0 },
    recentOrders: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const results = await Promise.allSettled([
          api.get('/orders?limit=100'),
          api.get('/products'),
          api.get('/clients'),
        ]);

        const ordersData = results[0].status === 'fulfilled' ? results[0].value.data : null;
        const productsData = results[1].status === 'fulfilled' ? results[1].value.data : null;
        const clientsData = results[2].status === 'fulfilled' ? results[2].value.data : null;

        const orders = ordersData?.data || [];
        const products = productsData?.data || productsData?.products || [];
        const clients = clientsData?.data || clientsData?.clients || [];

        // Agrupar órdenes por estado
        const byStatus = {};
        orders.forEach((o) => {
          byStatus[o.status] = (byStatus[o.status] || 0) + 1;
        });

        // Productos con stock bajo (menos de 10)
        const lowStock = products.filter(
          (p) => p.stock !== undefined && p.stock < 10
        ).length;

        setStats({
          orders: { total: orders.length, byStatus },
          products: { total: products.length, lowStock },
          clients: { total: clients.length },
          recentOrders: orders.slice(0, 8),
        });
      } catch (err) {
        console.error('Error cargando dashboard:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Métricas rápidas
  const activeOrders =
    (stats.orders.byStatus.created || 0) +
    (stats.orders.byStatus.accepted || 0) +
    (stats.orders.byStatus.preparing || 0) +
    (stats.orders.byStatus.prepared || 0) +
    (stats.orders.byStatus.in_delivery || 0);

  const pendingPayment = stats.orders.byStatus.delivered || 0;

  const cards = [
    {
      label: 'Órdenes Totales',
      value: stats.orders.total,
      icon: <HiOutlineClipboardList />,
      color: '#3b82f6',
      bg: '#eff6ff',
      link: '/ordenes',
    },
    {
      label: 'Órdenes Activas',
      value: activeOrders,
      icon: <HiOutlineClock />,
      color: '#f59e0b',
      bg: '#fffbeb',
      link: '/ordenes',
    },
    {
      label: 'Por Cobrar',
      value: pendingPayment,
      icon: <HiOutlineExclamationCircle />,
      color: '#ef4444',
      bg: '#fef2f2',
      link: '/ordenes',
    },
    {
      label: 'Productos',
      value: stats.products.total,
      icon: <HiOutlineCube />,
      color: '#8b5cf6',
      bg: '#f5f3ff',
      link: '/inventario',
    },
    {
      label: 'Clientes',
      value: stats.clients.total,
      icon: <HiOutlineUserGroup />,
      color: '#06b6d4',
      bg: '#ecfeff',
      link: '/clientes',
    },
    {
      label: 'Completadas',
      value: (stats.orders.byStatus.closed || 0) + (stats.orders.byStatus.paid || 0),
      icon: <HiOutlineCheckCircle />,
      color: '#22c55e',
      bg: '#f0fdf4',
      link: '/ordenes',
    },
  ];

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loader"></div>
        <p>Cargando dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Saludo */}
      <div className="dashboard-header">
        <div>
          <h1>Buen día, {user?.full_name?.split(' ')[0] || 'Usuario'}</h1>
          <p>
            {ROLE_LABELS[user?.role]} · Aquí tienes un resumen de tu negocio
          </p>
        </div>
      </div>

      {/* Tarjetas de métricas */}
      <div className="stats-grid">
        {cards.map((card) => (
          <div
            key={card.label}
            className="stat-card"
            onClick={() => navigate(card.link)}
          >
            <div className="stat-icon" style={{ background: card.bg, color: card.color }}>
              {card.icon}
            </div>
            <div className="stat-info">
              <span className="stat-value">{card.value}</span>
              <span className="stat-label">{card.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Sección inferior: Órdenes + Pipeline */}
      <div className="dashboard-grid">
        {/* Pipeline de estados */}
        <div className="dashboard-card">
          <h3>Pipeline de Órdenes</h3>
          <div className="pipeline">
            {Object.entries(STATUS_LABELS).map(([status, label]) => {
              const count = stats.orders.byStatus[status] || 0;
              if (status === 'cancelled') return null;
              return (
                <div key={status} className="pipeline-item">
                  <div className="pipeline-bar">
                    <div
                      className="pipeline-fill"
                      style={{
                        width: stats.orders.total
                          ? `${(count / stats.orders.total) * 100}%`
                          : '0%',
                        background: STATUS_COLORS[status],
                      }}
                    />
                  </div>
                  <div className="pipeline-info">
                    <span className="pipeline-label">{label}</span>
                    <span className="pipeline-count" style={{ color: STATUS_COLORS[status] }}>
                      {count}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Últimas órdenes */}
        <div className="dashboard-card">
          <div className="card-header-row">
            <h3>Últimas Órdenes</h3>
            <button className="link-btn" onClick={() => navigate('/ordenes')}>
              Ver todas →
            </button>
          </div>

          {stats.recentOrders.length === 0 ? (
            <div className="empty-state">
              <HiOutlineClipboardList className="empty-icon" />
              <p>No hay órdenes registradas</p>
            </div>
          ) : (
            <div className="orders-table-wrapper">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>Orden</th>
                    <th>Cliente</th>
                    <th>Estado</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentOrders.map((order) => (
                    <tr
                      key={order.id}
                      onClick={() => navigate(`/ordenes/${order.id}`)}
                    >
                      <td className="order-number">{order.order_number}</td>
                      <td>{order.client_name || `Cliente #${order.client_id}`}</td>
                      <td>
                        <span
                          className="status-badge"
                          style={{
                            background: `${STATUS_COLORS[order.status]}15`,
                            color: STATUS_COLORS[order.status],
                            borderColor: `${STATUS_COLORS[order.status]}30`,
                          }}
                        >
                          {STATUS_LABELS[order.status] || order.status}
                        </span>
                      </td>
                      <td className="order-total">
                        RD$ {Number(order.total_amount || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Acciones rápidas por rol */}
      {hasRole('salesman', 'admin') && (
        <div className="quick-actions">
          <h3>Acciones Rápidas</h3>
          <div className="actions-grid">
            <button className="action-btn" onClick={() => navigate('/ordenes/nueva')}>
              <HiOutlineClipboardList />
              Nueva Orden
            </button>
            <button className="action-btn" onClick={() => navigate('/clientes')}>
              <HiOutlineUserGroup />
              Ver Clientes
            </button>
            {hasRole('admin', 'manager') && (
              <button className="action-btn" onClick={() => navigate('/inventario')}>
                <HiOutlineCube />
                Inventario
              </button>
            )}
            {hasRole('admin', 'delivery') && (
              <button className="action-btn" onClick={() => navigate('/despacho')}>
                <HiOutlineTruck />
                Despacho
              </button>
            )}
            {hasRole('admin', 'accountant') && (
              <button className="action-btn" onClick={() => navigate('/comisiones')}>
                <HiOutlineCurrencyDollar />
                Comisiones
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
